import { DialogActions, DialogContent, DialogTitle } from '@mui/material'
import React, { forwardRef, ReactElement, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import Button from 'ui/atoms/Button/Button'
import { Dialog } from 'ui/components/Dialog/Dialog'
import MonacoStableDiffEditor from 'ui/components/MonacoStableDiffEditor/MonacoStableDiffEditor'
import styles from './UiConfigSaveControls.module.less'
import type { UiConfigSaveControlsHandle } from './UiConfigSaveControls.types'
import { useSaveControls } from './useSaveControls'

export type { UiConfigSaveControlsHandle }

type Props = {
  isEditorJsonInvalid?: boolean
}

export default forwardRef<UiConfigSaveControlsHandle, Props>(function UiConfigSaveControls(
  { isEditorJsonInvalid },
  ref
): ReactElement {
  const {
    rawUiConfig,
    hasDraft,
    isActionable,
    canDelete,
    effectiveDraftFields,
    activeDialog,
    handleSave,
    handlePublish,
    handleUnpublish,
    handleDeleteClick,
    handleConfirmDelete,
    handleConfirmSave,
    handleDismissDialog,
  } = useSaveControls()
  const { t } = useTranslation()

  useImperativeHandle(ref, () => ({
    triggerSave: () => {
      if (isActionable && hasDraft) handleSave()
    },
  }))

  if (!isActionable || !rawUiConfig) {
    return <></>
  }

  const savedFields = {
    display_name: rawUiConfig.display_name,
    props: rawUiConfig.props,
    children_ids: rawUiConfig.children_ids,
    dataset_instructions: rawUiConfig.dataset_instructions,
    alias: rawUiConfig.alias,
  }

  return (
    <div className={styles.UiConfigSaveControls}>
      <Button
        secondary
        onClick={handleSave}
        tooltip="ctrl + s"
        disabled={!hasDraft}
        disabledTooltip="No unsaved changes"
      >
        {t('Save')}
      </Button>

      {rawUiConfig.is_published ? (
        <Button danger onClick={handleUnpublish}>
          {t('Unpublish')}
        </Button>
      ) : (
        <Button
          danger
          secondary
          onClick={handlePublish}
          disabled={isEditorJsonInvalid}
          disabledTooltip="Fix JSON errors before publishing"
        >
          {t('Publish')}
        </Button>
      )}

      <Button danger onClick={handleDeleteClick} disabled={!canDelete} disabledTooltip="Config has not been saved yet">
        {t('Delete')}
      </Button>

      <Dialog open={activeDialog?.kind === 'delete'} onClose={handleDismissDialog}>
        <DialogTitle fontWeight="bold">{t('Delete')}</DialogTitle>
        <DialogContent>{t('Are you sure you want to delete all versions of this Ui Config?')}</DialogContent>
        <DialogActions>
          <Button secondary onClick={handleDismissDialog}>
            {t('Cancel')}
          </Button>
          <Button danger onClick={handleConfirmDelete}>
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activeDialog?.kind === 'warn-missing-vars'} onClose={handleDismissDialog}>
        <DialogTitle fontWeight="bold">{t('Save')}</DialogTitle>
        <DialogContent>
          <div>{activeDialog?.kind === 'warn-missing-vars' ? activeDialog.message : null}</div>
          <div>{t('Are you sure you want to save?')}</div>
        </DialogContent>
        <DialogActions>
          <Button secondary onClick={handleDismissDialog}>
            {t('Cancel')}
          </Button>
          <Button primary onClick={handleConfirmSave}>
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activeDialog?.kind === 'review-draft'} onClose={handleDismissDialog} fullWidth maxWidth="xl">
        <DialogTitle fontWeight="bold">{t('Review draft changes before saving')}</DialogTitle>
        <DialogContent>
          {activeDialog?.kind === 'review-draft' && activeDialog.warningMessage ? (
            <div>{activeDialog.warningMessage}</div>
          ) : null}
          <div>{t('Review the saved version on the left and your draft on the right before continuing.')}</div>
          {effectiveDraftFields ? (
            <MonacoStableDiffEditor
              height="70vh"
              language="json"
              original={JSON.stringify(savedFields, null, 2)}
              modified={JSON.stringify(effectiveDraftFields, null, 2)}
              options={{ readOnly: true }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button secondary onClick={handleDismissDialog}>
            {t('Cancel')}
          </Button>
          <Button primary onClick={handleConfirmSave}>
            {t(
              activeDialog?.kind === 'review-draft' && activeDialog.publishingUiConfig
                ? 'Publish changes'
                : 'Save changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
})
